<template>
  <aside class="admin-sidebar" :class="{ collapsed, open }" aria-label="Navigation principale">
    <router-link
      to="/dashboard"
      class="sidebar-brand"
      :aria-label="collapsed ? 'IBACY Net — Tableau de bord' : undefined"
      @click="$emit('navigate')"
      @mouseenter="showTooltip($event, 'IBACY Net')"
      @mouseleave="hideTooltip"
      @focus="showTooltip($event, 'IBACY Net')"
      @blur="hideTooltip"
    >
      <img src="/logo.png" alt="" class="brand-logo" />
      <span class="brand-text">IBACY <strong>Net</strong></span>
    </router-link>

    <nav class="sidebar-nav">
      <template v-for="section in sections" :key="section.title">
        <p v-if="!collapsed" class="sidebar-section">{{ section.title }}</p>
        <ul class="list-unstyled mb-2">
          <li v-for="item in section.items" :key="item.to">
            <router-link
              :to="item.to"
              class="sidebar-link"
              :class="{ active: isActive(item) }"
              :aria-current="isActive(item) ? 'page' : undefined"
              active-class=""
              exact-active-class=""
              :aria-label="collapsed ? item.label : undefined"
              @click="onNavigate"
              @mouseenter="showTooltip($event, item.label)"
              @mouseleave="hideTooltip"
              @focus="showTooltip($event, item.label)"
              @blur="hideTooltip"
            >
              <i :class="item.icon" aria-hidden="true"></i>
              <span>{{ item.label }}</span>
            </router-link>
          </li>
        </ul>
      </template>
    </nav>

    <div class="sidebar-footer">
      <small>IBACY Net · v2.0</small>
    </div>

    <Teleport to="body">
      <transition name="sidebar-tooltip">
        <div v-if="tooltip" class="sidebar-tooltip" role="tooltip" :style="{ top: `${tooltip.top}px`, left: `${tooltip.left}px` }">
          {{ tooltip.text }}
        </div>
      </transition>
    </Teleport>
  </aside>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { usePermissions } from "@/helpers/permissions";

const props = defineProps({
  collapsed: { type: Boolean, default: false },
  /** Menu ouvert en affichage mobile (≤ 991px). */
  open: { type: Boolean, default: false },
});
const emit = defineEmits(["navigate"]);

/**
 * Infobulle du menu replié : nom de l'entrée au survol ou au focus clavier. Positionnée en fixed
 * (téléportée dans body) pour ne pas être coupée par le défilement du menu.
 */
const tooltip = ref(null);

const showTooltip = (event, text) => {
  if (!props.collapsed) return;
  const rect = event.currentTarget.getBoundingClientRect();
  tooltip.value = { text, top: rect.top + rect.height / 2, left: rect.right + 10 };
};
const hideTooltip = () => {
  tooltip.value = null;
};
const onNavigate = () => {
  hideTooltip();
  emit("navigate");
};

watch(() => props.collapsed, hideTooltip);

const { can, canAny } = usePermissions();

const MENU = [
  {
    title: "Pilotage",
    items: [
      { to: "/dashboard", label: "Tableau de bord", icon: "fas fa-chart-pie", permission: "dashboard.view" },
      { to: "/notifications", label: "Notifications", icon: "fas fa-bell" },
    ],
  },
  {
    title: "Scolarité",
    items: [
      { to: "/etudiants", label: "Étudiants", icon: "fas fa-user-graduate", permission: "student.view" },
      { to: "/enseignants", label: "Enseignants", icon: "fas fa-chalkboard-user", permission: "teacher.view" },
      { to: "/absences", label: "Absences & discipline", icon: "fas fa-user-clock", permission: "absence.view" },
    ],
  },
  {
    title: "Évaluations",
    items: [
      { to: "/evaluations", label: "Évaluations & notes", icon: "fas fa-clipboard-list", permission: "evaluation.view", match: ["/evaluations", "/notes/saisie"] },
      { to: "/notes/releve", label: "Relevé des notes", icon: "fas fa-table-list", permission: "grade.view" },
      { to: "/demandes", label: "Demandes", icon: "fas fa-file-signature", permission: "request.view" },
      { to: "/rapports", label: "Bulletins & rapports", icon: "fas fa-file-invoice", permission: "report.view" },
      { to: "/signatures-bulletins", label: "Signature des bulletins", icon: "fas fa-signature", permission: ["signature.view", "signature.sign"] },
    ],
  },
  {
    title: "Administration",
    items: [
      { to: "/parametres", label: "Paramétrage", icon: "fas fa-sliders", permission: ["academic_year.view", "level.view", "promotion.view", "group.view", "subject.view", "programme.view", "setting.view"] },
      { to: "/utilisateurs", label: "Utilisateurs", icon: "fas fa-users-gear", permission: "user.view" },
      { to: "/roles", label: "Rôles & permissions", icon: "fas fa-user-shield", permission: "role.view" },
      { to: "/audit", label: "Journal d'audit", icon: "fas fa-shield-halved", permission: "audit.view" },
      { to: "/licence", label: "Licence", icon: "fas fa-key", permission: ["license.status", "license.activate", "license.view", "license.create"] },
    ],
  },
  {
    title: "Assistance",
    items: [{ to: "/aide", label: "Aide et guide", icon: "fas fa-circle-question" }],
  },
];

const route = useRoute();

/**
 * Menu actif sur sa page et toutes ses sous-pages (ex. /etudiants/12/modifier → Étudiants).
 * « match » liste les chemins rattachés à un menu sans en partager le préfixe (ex. /notes/saisie → Évaluations & notes).
 */
const isActive = (item) =>
  (item.match || [item.to]).some((prefix) => route.path === prefix || route.path.startsWith(`${prefix}/`));

const allowed = (permission) => !permission || (Array.isArray(permission) ? canAny(permission) : can(permission));

const sections = computed(() =>
  MENU.map((section) => ({ ...section, items: section.items.filter((item) => allowed(item.permission)) })).filter((section) => section.items.length)
);
</script>

<style>
.sidebar-tooltip {
  position: fixed;
  z-index: 2200;
  transform: translateY(-50%);
  padding: 0.35rem 0.65rem;
  border-radius: calc(var(--radius) - 2px);
  background: hsl(var(--foreground));
  color: hsl(var(--background));
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: var(--shadow-sm);
}
.sidebar-tooltip::before {
  content: "";
  position: absolute;
  top: 50%;
  left: -4px;
  width: 8px;
  height: 8px;
  background: inherit;
  transform: translateY(-50%) rotate(45deg);
}
.sidebar-tooltip-enter-active,
.sidebar-tooltip-leave-active {
  transition: opacity 0.12s ease;
}
.sidebar-tooltip-enter-from,
.sidebar-tooltip-leave-to {
  opacity: 0;
}
</style>

<style scoped>
.admin-sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 1050;
  width: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  background: hsl(var(--card));
  border-right: 1px solid hsl(var(--border));
  transition: width 0.25s ease, transform 0.25s ease;
}
.sidebar-brand {
  height: 64px;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0 1.15rem;
  border-bottom: 1px solid hsl(var(--border));
  color: hsl(var(--foreground));
  font-size: 1.1rem;
  text-decoration: none;
}
.brand-text strong {
  color: hsl(var(--primary));
}
.brand-logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
}
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.85rem 0.7rem;
}
.sidebar-section {
  margin: 0.6rem 0.6rem 0.35rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: hsl(var(--muted-foreground));
}
.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.75rem;
  margin-bottom: 0.15rem;
  border-radius: var(--radius);
  color: hsl(var(--muted-foreground));
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-link i {
  width: 1.2rem;
  text-align: center;
}
.sidebar-link:hover {
  background: hsl(var(--accent));
  color: hsl(var(--accent-foreground));
}
.sidebar-link.active {
  background: hsl(var(--primary) / 0.12);
  color: hsl(var(--primary));
  font-weight: 600;
}
.sidebar-link:focus-visible {
  outline: 2px solid hsl(var(--primary));
  outline-offset: 1px;
}
.sidebar-footer {
  padding: 0.85rem;
  border-top: 1px solid hsl(var(--border));
  text-align: center;
  color: hsl(var(--muted-foreground));
}
.collapsed {
  width: var(--sidebar-collapsed-width);
}
.collapsed .brand-text,
.collapsed .sidebar-link span,
.collapsed .sidebar-footer {
  display: none;
}
.collapsed .sidebar-brand,
.collapsed .sidebar-link {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
@media (max-width: 991px) {
  .admin-sidebar,
  .admin-sidebar.collapsed {
    width: var(--sidebar-width);
    transform: translateX(-100%);
  }
  .admin-sidebar.open {
    transform: none;
    box-shadow: 0 16px 36px rgb(0 0 0 / 0.18);
  }
  .collapsed .brand-text,
  .collapsed .sidebar-link span,
  .collapsed .sidebar-footer {
    display: initial;
  }
}
</style>
